import { useState } from 'react';
import { Link } from 'react-router-dom';
import { channelLabel, type AutomationTriggerType, type Channel } from '@neuroedge/shared';
import { Button, Select, TextField } from '../../components/ui';
import { LocalizedTextInput } from '../../components/localized-text/LocalizedTextInput';
import {
  ACTION_KINDS,
  RULE_KINDS,
  TIME_BASED_TRIGGERS,
  TRIGGERS,
  blankRule,
  templateVariableKeys,
  toPayload,
  type ActionDraft,
  type ActionKind,
  type AutomationFormState,
  type RuleKind,
} from './automation-model';
import {
  AdvancedRulesEditor,
  FieldError,
  LinkButton,
  ReplyOptionsEditor,
  RuleEditor,
  box,
  muted,
} from './automation-form-parts';

/**
 * The When / If / Then editors, shared by the form view
 * (`AutomationFormPage`) and the visual builder's side panel
 * (`AutomationBuilder`) - one set of fields, so both views always edit the
 * same thing the same way.
 */

export function ChoiceCard({
  selected,
  title,
  hint,
  onClick,
  role = 'checkbox',
}: {
  selected: boolean;
  title: string;
  hint: string;
  onClick: () => void;
  role?: 'checkbox' | 'button';
}) {
  return (
    <button
      type="button"
      role={role}
      aria-checked={role === 'checkbox' ? selected : undefined}
      onClick={onClick}
      style={{
        textAlign: 'left',
        display: 'grid',
        gap: 2,
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        border: `2px solid ${selected ? 'var(--color-primary)' : 'var(--color-border)'}`,
        background: selected ? 'var(--color-surface-raised)' : 'var(--color-surface)',
        color: 'var(--color-text)',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      <strong>
        {role === 'checkbox' ? (selected ? '☑ ' : '☐ ') : ''}
        {title}
      </strong>
      <span style={muted}>{hint}</span>
    </button>
  );
}

export const cardGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 'var(--space-2)',
} as const;

type Set = (patch: Partial<AutomationFormState>) => void;
type Clear = (...keys: string[]) => void;

/** 1. When - triggers (several allowed) and, for message triggers, the channel. */
export function WhenEditor({
  form,
  set,
  clear,
  errors,
  channels,
}: {
  form: AutomationFormState;
  set: Set;
  clear: Clear;
  errors: Record<string, string>;
  channels: Channel[];
}) {
  const timeBased = form.triggers.some((t) => TIME_BASED_TRIGGERS.has(t));
  function toggle(t: AutomationTriggerType) {
    set({
      triggers: form.triggers.includes(t)
        ? form.triggers.filter((x) => x !== t)
        : [...form.triggers, t],
    });
    clear('triggers');
  }
  return (
    <>
      <div style={cardGrid}>
        {TRIGGERS.map((t) => (
          <ChoiceCard
            key={t.value}
            selected={form.triggers.includes(t.value)}
            title={t.title}
            hint={t.hint}
            onClick={() => toggle(t.value)}
          />
        ))}
      </div>
      <FieldError message={errors.triggers} />
      {(channels.length > 1 || form.channelId) && !timeBased ? (
        <Select
          label="On which channel?"
          value={form.channelId}
          onChange={(e) => set({ channelId: e.target.value })}
          options={[
            // No channel = every WhatsApp number; a website chat only runs
            // automations set on it (ADR-038).
            { value: '', label: 'All my WhatsApp numbers' },
            ...channels.map((c) => ({ value: c.id, label: channelLabel(c) })),
          ]}
        />
      ) : null}
    </>
  );
}

/** The simple rules as raw groups, so "Advanced rules" starts from what's already there. */
function groupsFromPayload(form: AutomationFormState): AutomationFormState['conditionGroups'] {
  const groups = new Map<number, AutomationFormState['conditionGroups'][number]>();
  for (const c of toPayload(form).conditions ?? []) {
    groups.set(c.groupIndex, [
      ...(groups.get(c.groupIndex) ?? []),
      { field: c.field, operator: c.operator, value: c.value ?? '' },
    ]);
  }
  return [...groups.values()];
}

/** 2. If - simple rules with an all/any switch, or raw AND/OR groups. */
export function IfEditor({
  form,
  set,
  clear,
  errors,
  replyOptions,
}: {
  form: AutomationFormState;
  set: Set;
  clear: Clear;
  errors: Record<string, string>;
  replyOptions: { id: string; title: string; from: string }[];
}) {
  if (form.rulesMode === 'advanced') {
    return (
      <>
        <AdvancedRulesEditor
          groups={form.conditionGroups}
          onChange={(conditionGroups) => set({ conditionGroups })}
          errors={errors}
        />
        <div>
          <LinkButton
            onClick={() => {
              if (
                window.confirm(
                  'Switch to simple rules? Your current advanced rules will be cleared.',
                )
              ) {
                set({ rulesMode: 'simple', rules: [], conditionGroups: [] });
              }
            }}
          >
            Switch to simple rules
          </LinkButton>
        </div>
      </>
    );
  }
  return (
    <>
      {form.rules.length > 1 ? (
        <div
          style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <span>Run when</span>
          <Button
            type="button"
            variant={form.match === 'any' ? 'primary' : 'secondary'}
            onClick={() => set({ match: 'any' })}
          >
            any rule matches
          </Button>
          <Button
            type="button"
            variant={form.match === 'all' ? 'primary' : 'secondary'}
            onClick={() => set({ match: 'all' })}
          >
            all rules match
          </Button>
        </div>
      ) : null}
      {form.rules.map((rule, i) => (
        <RuleEditor
          key={i}
          rule={rule}
          replyOptions={replyOptions}
          error={errors[`conditions/${i}/value`]}
          onChange={(next) => {
            set({ rules: form.rules.map((r, j) => (j === i ? next : r)) });
            clear(`conditions/${i}/value`);
          }}
          onRemove={() => set({ rules: form.rules.filter((_, j) => j !== i) })}
        />
      ))}
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'end' }}>
        <div style={{ minWidth: 260 }}>
          <Select
            label="Add a rule"
            value=""
            onChange={(e) => {
              if (!e.target.value) return;
              set({ rules: [...form.rules, blankRule(e.target.value as RuleKind)] });
            }}
            options={[
              { value: '', label: 'Choose…' },
              ...RULE_KINDS.filter((k) => k.addable).map((k) => ({
                value: k.kind,
                label: k.label,
              })),
            ]}
          />
        </div>
        <LinkButton
          onClick={() =>
            set({
              rulesMode: 'advanced',
              conditionGroups: toPayload(form).conditions?.length
                ? groupsFromPayload(form)
                : [[{ field: 'message.text', operator: 'CONTAINS_ANY', value: '' }]],
            })
          }
        >
          Advanced rules (AND / OR)
        </LinkButton>
      </div>
    </>
  );
}

/** "What should happen?" - one card per action kind. */
export function AddActionPicker({
  onPick,
  onCancel,
}: {
  onPick: (kind: ActionKind) => void;
  onCancel: () => void;
}) {
  return (
    <div style={box}>
      <strong>What should happen?</strong>
      <div style={cardGrid}>
        {ACTION_KINDS.map((k) => (
          <ChoiceCard
            key={k.kind}
            role="button"
            selected={false}
            title={k.title}
            hint={k.hint}
            onClick={() => onPick(k.kind)}
          />
        ))}
      </div>
      <div>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export interface ActionCardLookups {
  templates: { name: string; language: string; components: unknown }[];
  templatesLoading: boolean;
  flows: { key: string; name: string; description: string | null }[];
  pipelines: { id: string; name: string; stages: { id: string; name: string }[] }[];
  /**
   * A template belongs to no workspace: its "add as a lead" has no pipeline
   * (the tenant picks one when using it), so the pipeline / stage pickers are
   * hidden.
   */
  noPipeline?: boolean;
  /**
   * No channel to list approved templates from (a template library entry):
   * the WhatsApp template name is typed instead of picked.
   */
  freeTemplateNames?: boolean;
}

/** One "Then" step - its fields depend on the kind of action. */
export function ActionCard({
  index,
  count,
  action,
  errors,
  clear,
  onChange,
  onMove,
  onRemove,
  timeBased,
  lookups,
}: {
  index: number;
  count: number;
  action: ActionDraft;
  errors: Record<string, string>;
  clear: Clear;
  onChange: (next: ActionDraft) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
  timeBased: boolean;
  lookups: ActionCardLookups;
}) {
  const { templates, templatesLoading, flows, pipelines } = lookups;
  const [showAdvanced, setShowAdvanced] = useState(Boolean(action.fallbackTemplateName));
  const key = (field: string) => `actions/${index}/${field}`;
  const meta = ACTION_KINDS.find((k) => k.kind === action.kind)!;
  const patch = (p: Partial<ActionDraft>, ...errorFields: string[]) => {
    onChange({ ...action, ...p });
    clear(...errorFields.map(key));
  };
  const templateOptions = [
    { value: '', label: templatesLoading ? 'Loading templates…' : 'Choose an approved template' },
    ...templates.map((t) => ({ value: t.name, label: `${t.name} (${t.language})` })),
    ...(action.templateName && !templates.some((t) => t.name === action.templateName)
      ? [{ value: action.templateName, label: action.templateName }]
      : []),
  ];
  const sendsMessage = action.kind === 'reply' || action.kind === 'image';

  return (
    <div style={box} aria-label={`Step ${index + 1}: ${meta.title}`} role="group">
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}
      >
        <strong>
          {index + 1}. {meta.title}
        </strong>
        <span style={{ flex: 1 }} />
        <Button
          type="button"
          variant="secondary"
          disabled={index === 0}
          onClick={() => onMove(-1)}
          aria-label="Move up"
        >
          ↑
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={index === count - 1}
          onClick={() => onMove(1)}
          aria-label="Move down"
        >
          ↓
        </Button>
        <Button type="button" variant="secondary" onClick={onRemove}>
          Remove
        </Button>
      </div>

      {action.kind === 'reply' ? (
        <>
          <LocalizedTextInput
            label="Message"
            value={action.text}
            multiline
            maxLength={action.replyMode === 'none' ? 4096 : 1024}
            onChange={(text) => patch({ text }, 'text')}
          />
          <FieldError message={errors[key('text')]} />
          <ReplyOptionsEditor
            action={action}
            onChange={(next) => {
              onChange(next);
              clear(key('buttons'), key('sections'), key('buttonText'));
            }}
            errorKey={key}
            errors={errors}
          />
        </>
      ) : null}

      {action.kind === 'image' ? (
        <>
          <TextField
            label="Image link"
            placeholder="https://…"
            value={action.imageUrl}
            onChange={(e) => patch({ imageUrl: e.target.value }, 'imageUrl')}
            error={errors[key('imageUrl')]}
          />
          <LocalizedTextInput
            label="Caption (optional)"
            value={action.caption}
            multiline
            maxLength={1024}
            onChange={(caption) => patch({ caption }, 'caption')}
          />
          <FieldError message={errors[key('caption')]} />
        </>
      ) : null}

      {action.kind === 'template' ? (
        <>
          {lookups.freeTemplateNames ? (
            <TextField
              label="Template name"
              placeholder="e.g. order_update"
              value={action.templateName}
              onChange={(e) => patch({ templateName: e.target.value }, 'templateName')}
              error={errors[key('templateName')]}
            />
          ) : (
            <Select
              label="Template"
              value={action.templateName}
              onChange={(e) =>
                patch({ templateName: e.target.value, variables: {} }, 'templateName')
              }
              options={templateOptions}
              error={errors[key('templateName')]}
            />
          )}
          {(() => {
            const chosen = templates.find((t) => t.name === action.templateName);
            const keys = chosen
              ? templateVariableKeys(chosen.components)
              : Object.keys(action.variables);
            return keys.map((k) => (
              <TextField
                key={k}
                label={`Value for {{${k}}}`}
                value={action.variables[k] ?? ''}
                onChange={(e) => patch({ variables: { ...action.variables, [k]: e.target.value } })}
              />
            ));
          })()}
          <span style={muted}>
            Templates are pre-approved by Meta, so they can be sent even 24 hours after the
            customer's last message.
          </span>
        </>
      ) : null}

      {action.kind === 'flow' ? (
        <>
          <Select
            label="Conversation flow"
            value={action.flowKey}
            onChange={(e) => patch({ flowKey: e.target.value }, 'flowKey')}
            options={[
              { value: '', label: 'Choose a flow' },
              ...flows.map((f) => ({ value: f.key, label: f.name })),
              ...(action.flowKey && !flows.some((f) => f.key === action.flowKey)
                ? [{ value: action.flowKey, label: action.flowKey }]
                : []),
            ]}
            error={errors[key('flowKey')]}
          />
          {flows.find((f) => f.key === action.flowKey)?.description ? (
            <span style={muted}>{flows.find((f) => f.key === action.flowKey)!.description}</span>
          ) : null}
          {lookups.freeTemplateNames ? null : (
            <Link to="/automation-flows" style={{ fontSize: 'var(--font-size-sm)' }}>
              See and customize flows →
            </Link>
          )}
        </>
      ) : null}

      {action.kind === 'lead' ? (
        <>
          {lookups.noPipeline ? (
            <span style={muted}>
              The workspace picks the pipeline and stage when it uses this template.
            </span>
          ) : (
            <div
              style={{
                display: 'grid',
                gap: 'var(--space-2)',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              }}
            >
              <Select
                label="Pipeline"
                value={action.pipelineId}
                onChange={(e) => {
                  const p = pipelines.find((x) => x.id === e.target.value);
                  patch(
                    { pipelineId: e.target.value, stageId: p?.stages[0]?.id ?? '' },
                    'pipelineId',
                    'stageId',
                  );
                }}
                options={[
                  { value: '', label: 'Choose a pipeline' },
                  ...pipelines.map((p) => ({ value: p.id, label: p.name })),
                ]}
                error={errors[key('pipelineId')]}
              />
              <Select
                label="Stage"
                value={action.stageId}
                disabled={!action.pipelineId}
                onChange={(e) => patch({ stageId: e.target.value }, 'stageId')}
                options={[
                  { value: '', label: 'Choose a stage' },
                  ...(pipelines.find((p) => p.id === action.pipelineId)?.stages ?? []).map((s) => ({
                    value: s.id,
                    label: s.name,
                  })),
                ]}
                error={errors[key('stageId')]}
              />
            </div>
          )}
          <LinkButton onClick={() => setShowAdvanced((s) => !s)} expanded={showAdvanced}>
            {showAdvanced ? '▾' : '▸'} Lead title and source
          </LinkButton>
          {showAdvanced ? (
            <>
              <TextField
                label="Lead title (optional)"
                value={action.leadTitle}
                onChange={(e) => patch({ leadTitle: e.target.value }, 'title')}
                error={errors[key('title')]}
              />
              <TextField
                label="Lead source (optional)"
                value={action.leadSource}
                onChange={(e) => patch({ leadSource: e.target.value }, 'source')}
                error={errors[key('source')]}
              />
            </>
          ) : null}
        </>
      ) : null}

      {action.kind === 'quotation' ? (
        <span style={muted}>
          Reads the photo, PDF or typed list the customer just sent, prices it from your Products
          &amp; Services, and replies with a quotation PDF. Must be enabled for your account.
        </span>
      ) : null}

      {sendsMessage && !timeBased ? (
        <>
          <LinkButton onClick={() => setShowAdvanced((s) => !s)} expanded={showAdvanced}>
            {showAdvanced ? '▾' : '▸'} Advanced: after 24 hours
          </LinkButton>
          {showAdvanced ? (
            <>
              <span style={muted}>
                WhatsApp only allows free-form replies within 24 hours of the customer's last
                message. Pick an approved template to send instead after that.
              </span>
              {lookups.freeTemplateNames ? (
                <TextField
                  label="Template to send after 24 hours (optional)"
                  value={action.fallbackTemplateName}
                  onChange={(e) =>
                    patch({ fallbackTemplateName: e.target.value }, 'fallbackTemplateName')
                  }
                  error={errors[key('fallbackTemplateName')]}
                />
              ) : (
                <Select
                  label="Template to send after 24 hours (optional)"
                  value={action.fallbackTemplateName}
                  onChange={(e) =>
                    patch({ fallbackTemplateName: e.target.value }, 'fallbackTemplateName')
                  }
                  options={[
                    { value: '', label: 'None - skip sending' },
                    ...templateOptions.slice(1),
                  ]}
                  error={errors[key('fallbackTemplateName')]}
                />
              )}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
